import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Modal,
  Tooltip,
  Fade,
  Collapse,
} from "@mui/material";
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  AnimatePresence,
} from "framer-motion";
import {
  logGameEnded,
  logGameShared,
  logGuessMade,
  logFeedbackGiven,
} from "../../../analytics";
import { GameHeader } from "../../WordyVerse/Components/GameBoard/GameHeader";
import Footer from "../../../components/Footer";
import { checkWordValidity } from "../../../utils/dictionary";
import KeyIcon from "@mui/icons-material/Key";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import {
  Send as SendIcon,
  HelpCircle,
  ChevronDown,
  Check,
  Trophy,
  Star,
} from "lucide-react";
import Confetti from "react-confetti";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { Howl } from "howler";

const BaseSlidewords = ({
  title = "Slidewords",
  customTitle,
  iconPath,
  subtitle = "Slide each word horizontally to form new words and find the target number!",
  shareText = "I played Slidewords",
  shareUrl = window.location.href,
  bannerPath,
  publicationId = null,
  publicationName = "Slidewords",
  buttonColor = "black",
  modalBackgroundColor = "white",
  titleColor = "black",
  backgroundColor = "white",
  // Game configuration
  goalWords = 2, // Number of words needed to win
  wordList = ["SLIDE", "WORDS", "PUZZLE", "BRAIN", "GAMES"],
  maxAttempts = 20, // Optional limit on attempts
  onGameUpdate,
  showHowToOverride,
  setShowHowToOverride,
  allPossibleWords, // New prop for all possible answers
  isMuted: externalIsMuted, // External mute state from parent
}) => {
  // State for storing guesses and scores
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [invalidWordError, setInvalidWordError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [shakeAnimation, setShakeAnimation] = useState(false);
  const isAnimating = useRef(false);

  // Game state
  const [wordsFound, setWordsFound] = useState(0); // Changed from currentScore
  const [attempts, setAttempts] = useState(0);
  const [usedWords, setUsedWords] = useState(new Set()); // Track used words to prevent duplicates
  const [hasWon, setHasWon] = useState(false); // Track if player has won but can continue

  // Add state for victory modal
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [victoryModalDismissed, setVictoryModalDismissed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState(200);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [hasViewedAnswers, setHasViewedAnswers] = useState(false);
  const [hasCelebratedAllFound, setHasCelebratedAllFound] = useState(false);

  // Add state for how-to-play modal
  const [showHowToPlay, setShowHowToPlay] = useState(false);

  // Add state for reveal confirmation dialog
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);

  // Add state for collapsible found words section
  const [foundWordsExpanded, setFoundWordsExpanded] = useState(false);

  // NEW: Juicy word found animation state
  const [juicyWordFound, setJuicyWordFound] = useState(null);
  const [showWordGlow, setShowWordGlow] = useState(false);
  const [showParticles, setShowParticles] = useState(false);
  const [particles, setParticles] = useState([]);
  const [showFloatingBadge, setShowFloatingBadge] = useState(false);
  const [floatingBadgeData, setFloatingBadgeData] = useState({
    word: "",
    position: { x: 0, y: 0 },
  });

  // Add sound control state
  const [isMuted, setIsMuted] = useState(false);

  // Use external mute state if provided, otherwise use internal state
  const effectiveIsMuted =
    externalIsMuted !== undefined ? externalIsMuted : isMuted;

  // Add sound instances
  const clickSound = useRef(
    new Howl({
      src: ["/assets/audio/click.ogg", "/assets/audio/click.mp3"],
      volume: 0.5,
    })
  );
  const errorSound = useRef(
    new Howl({
      src: ["/assets/audio/error.ogg", "/assets/audio/error.mp3"],
      volume: 0.2,
    })
  );
  const successSound = useRef(
    new Howl({
      src: ["/assets/audio/level-win.mp3"],
      volume: 0.3,
    })
  );
  const buttonClickSound = useRef(
    new Howl({
      src: ["/assets/audio/click-button.ogg", "/assets/audio/click-button.mp3"],
      volume: 0.2,
    })
  );
  const slideSound = useRef(
    new Howl({
      src: ["/assets/audio/slide.ogg", "/assets/audio/slide.mp3"],
      volume: 0.3,
    })
  );

  // Add effect to handle mute state
  useEffect(() => {
    clickSound.current.mute(effectiveIsMuted);
    errorSound.current.mute(effectiveIsMuted);
    successSound.current.mute(effectiveIsMuted);
    buttonClickSound.current.mute(effectiveIsMuted);
    slideSound.current.mute(effectiveIsMuted);
  }, [effectiveIsMuted]);

  // Add toggle mute function
  const toggleMute = () => {
    // Only toggle internal state if external state is not provided
    if (externalIsMuted === undefined) {
      setIsMuted(!isMuted);
    }
  };

  // Create individual motion values at the top level
  const motion1 = useMotionValue(0);
  const motion2 = useMotionValue(0);
  const motion3 = useMotionValue(0);
  const motion4 = useMotionValue(0);
  const motion5 = useMotionValue(0);

  // Collect all motion values in an array we can reference
  const motionValues = useMemo(() => {
    return [motion1, motion2, motion3, motion4, motion5];
  }, [motion1, motion2, motion3, motion4, motion5]);

  // Create refs for each word's dimensions
  const wordRefs = useRef(
    Array(wordList.length)
      .fill()
      .map((_, i) => ({
        x: motionValues[i],
        width: 0,
        letterWidth: 0,
      }))
  );

  // State for storing the width of each letter cell
  const [letterWidth, setLetterWidth] = useState(60);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const isInitialSetup = useRef(true);
  const [isLayoutReady, setIsLayoutReady] = useState(false);

  // Use allPossibleWords if provided, otherwise fallback to wordList
  const possibleWords =
    allPossibleWords && allPossibleWords.length > 0
      ? allPossibleWords
      : wordList;
  const totalPossible = possibleWords.length;
  const foundWordsSet = new Set(guesses.map((g) => g.word.toUpperCase()));
  const numWordsFound = guesses.filter((g) =>
    possibleWords.includes(g.word.toUpperCase())
  ).length;
  const remaining = totalPossible - numWordsFound;
  const allFound = numWordsFound === totalPossible;

  // Share preview string
  const sharePreview = `I found ${numWordsFound}/${totalPossible} words in today's Slidewords! 🎉 #Slidewords`;

  // Effect to call onGameUpdate when score, guesses, or attempts change
  useEffect(() => {
    if (onGameUpdate) {
      onGameUpdate({
        score: numWordsFound,
        guesses: guesses.length,
        attempts,
      });
    }
  }, [numWordsFound, guesses, attempts, onGameUpdate]);

  // Show how-to-play dialog when component mounts or via override
  useEffect(() => {
    if (showHowToOverride !== undefined) {
      setShowHowToPlay(showHowToOverride);
    } else {
      setShowHowToPlay(true);
    }
  }, [showHowToOverride]);

  // Allow parent to control closing the how-to modal
  useEffect(() => {
    if (setShowHowToOverride) {
      setShowHowToOverride(showHowToPlay);
    }
  }, [showHowToPlay, setShowHowToOverride]);

  // Calculate the appropriate dimensions on mount and window resize
  useEffect(() => {
    const calculateDimensions = () => {
      if (!containerRef.current || !wordList || wordList.length === 0) {
        return;
      }
      // Hide content while calculating
      if (isInitialSetup.current) {
        setIsLayoutReady(false);
      }

      const width = containerRef.current.offsetWidth;
      setContainerWidth(width);

      // Calculate letter width based on the longest word
      const maxWordLength = Math.max(...wordList.map((word) => word.length));
      const calculatedWidth = Math.min(60, (width - 40) / maxWordLength);
      setLetterWidth(calculatedWidth);

      // Update refs with new dimensions
      wordRefs.current = wordRefs.current.map((ref, index) => {
        const wordLength = wordList[index].length;
        return {
          ...ref,
          width: wordLength * calculatedWidth,
          letterWidth: calculatedWidth,
        };
      });

      // If this is the initial setup, position words to have specific letters in center
      if (isInitialSetup.current) {
        isInitialSetup.current = false;

        // Position words immediately but keep content hidden
        const centerOffset = width / 2 - calculatedWidth / 2;

        // Set each word to align the correct center letter (left-middle for even, true center for odd)
        wordList.forEach((word, index) => {
          const wordLength = word.length;
          // For even, pick left-middle; for odd, pick true center
          const centerLetterIndex =
            wordLength % 2 === 0
              ? wordLength / 2 - 1
              : Math.floor(wordLength / 2);
          const targetPosition =
            centerOffset - calculatedWidth * centerLetterIndex;
          motionValues[index].set(targetPosition);
        });

        // Short delay to ensure DOM updates, then show content
        setTimeout(() => {
          setIsLayoutReady(true);
        }, 100);
      }
    };

    calculateDimensions();

    window.addEventListener("resize", calculateDimensions);
    return () => window.removeEventListener("resize", calculateDimensions);
  }, [wordList, motionValues]);

  // Sync wordRefs.current to wordList length and letterWidth
  useEffect(() => {
    wordRefs.current = Array(wordList.length)
      .fill()
      .map((_, i) => ({
        x: motionValues[i],
        width: 0,
        letterWidth: letterWidth,
      }));
  }, [wordList, motionValues, letterWidth]);

  // Snap position to closest letter grid after drag ends
  const snapToGrid = (index, position) => {
    if (!wordRefs.current[index]) return; // guard
    const letterW = wordRefs.current[index].letterWidth;
    const centerX = containerWidth / 2;

    // Calculate offset from the exact center of the grid
    const offsetFromCenter = centerX - letterW / 2 - position;

    // Calculate how many letter steps away we are
    const letterSteps = Math.round(offsetFromCenter / letterW);

    // Calculate target position to snap to grid
    const snapPosition = centerX - letterW / 2 - letterSteps * letterW;

    // Word constraints to ensure a letter is always in center column
    const wordLength = wordList[index].length;
    const maxRight = centerX - letterW / 2; // First letter at center
    const maxLeft = maxRight - (wordLength - 1) * letterW; // Last letter at center

    // Ensure position is within bounds
    const clampedPosition = Math.min(Math.max(snapPosition, maxLeft), maxRight);

    // Play slide sound when snapping to grid
    slideSound.current.play();

    // Animate to the snapped position
    animate(motionValues[index], clampedPosition, {
      type: "spring",
      damping: 20,
      stiffness: 300,
    });

    return clampedPosition;
  };

  // Extract the current center letter from each word based on its position
  const extractGuess = () => {
    const centerLetters = wordList.map((word, index) => {
      const xPos = motionValues[index].get();
      const letterW = wordRefs.current[index].letterWidth;
      const centerX = containerWidth / 2;

      // Calculate which letter is in the center
      // For the first letter to be at center, xPos = centerX - letterW/2
      // So letter index = (centerX - letterW/2 - xPos) / letterW
      const letterIndex = Math.round((centerX - letterW / 2 - xPos) / letterW);

      // Ensure index is within bounds
      if (letterIndex >= 0 && letterIndex < word.length) {
        return word[letterIndex];
      }
      return "";
    });

    return centerLetters.join("");
  };

  // Effect to handle victory celebration with confetti
  useEffect(() => {
    if (
      (numWordsFound === totalPossible || hasViewedAnswers) &&
      !showVictoryModal &&
      !victoryModalDismissed
    ) {
      setShowVictoryModal(true);
      setShowConfetti(true);
      setConfettiPieces(200);

      // Confetti fade-out: show for 2s, then fade out over 1s
      const duration = 2000;
      const fadeOutTime = 1000;
      const fadeOutInterval = 50;
      const decrementSteps = fadeOutTime / fadeOutInterval;
      const decrementAmount = Math.ceil(200 / decrementSteps);

      // Start the fade-out after the duration
      const timer = setTimeout(() => {
        const fadeInterval = setInterval(() => {
          setConfettiPieces((prev) => {
            const newValue = prev - decrementAmount;
            if (newValue <= 0) {
              clearInterval(fadeInterval);
              setShowConfetti(false);
              return 0;
            }
            return newValue;
          });
        }, fadeOutInterval);
      }, duration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [
    numWordsFound,
    totalPossible,
    hasViewedAnswers,
    showVictoryModal,
    victoryModalDismissed,
  ]);

  // Effect to handle 'YOU FOUND THEM ALL!' celebration
  useEffect(() => {
    if (
      allFound &&
      !hasViewedAnswers &&
      !hasCelebratedAllFound &&
      !showVictoryModal // don't show if another modal is open
    ) {
      setShowVictoryModal(true);
      setShowConfetti(true);
      setHasCelebratedAllFound(true);
      // ... confetti logic ...
    }
  }, [allFound, hasViewedAnswers, hasCelebratedAllFound, showVictoryModal]);

  // Check the guess and update the game state
  const checkGuess = async () => {
    const extractedGuess = extractGuess();

    if (extractedGuess.length === wordList.length) {
      // Check if the word has already been used
      if (usedWords.has(extractedGuess.toLowerCase())) {
        if (!isAnimating.current) {
          isAnimating.current = true;
          setInvalidWordError(true);
          setErrorMessage("Word already used!");
          setShakeAnimation(true);

          // Play error sound for duplicate word
          errorSound.current.play();

          // Perform shake animation on each word
          wordList.forEach((_, index) => {
            const currentX = motionValues[index].get();

            // Create a shake sequence using animate
            animate(
              motionValues[index],
              [
                currentX,
                currentX - 10,
                currentX + 10,
                currentX - 10,
                currentX + 10,
                currentX,
              ],
              { duration: 0.4, ease: "easeInOut" }
            );
          });

          // Reset shake animation after it completes
          setTimeout(() => {
            setShakeAnimation(false);

            // Make sure all words are properly snapped back to grid
            wordList.forEach((_, index) => {
              snapToGrid(index, motionValues[index].get());
            });

            // Reset animation state
            isAnimating.current = false;
          }, 500);

          // Reset error message after a delay
          setTimeout(() => {
            setInvalidWordError(false);
          }, 3000);
        }
        return;
      }

      // NEW: Check if this is a valid word from our possible words list
      const isValidTargetWord = possibleWords.includes(
        extractedGuess.toUpperCase()
      );

      // Check if the guess is a valid word
      try {
        const isValid = await checkWordValidity(extractedGuess.toLowerCase());

        if (!isValid) {
          // Only start shake animation if not already animating
          if (!isAnimating.current) {
            isAnimating.current = true;
            setInvalidWordError(true);
            setErrorMessage("Not a valid word!");
            setShakeAnimation(true);

            // Play error sound for invalid word
            errorSound.current.play();

            // Perform shake animation on each word
            wordList.forEach((_, index) => {
              const currentX = motionValues[index].get();

              // Create a shake sequence using animate
              animate(
                motionValues[index],
                [
                  currentX,
                  currentX - 10,
                  currentX + 10,
                  currentX - 10,
                  currentX + 10,
                  currentX,
                ],
                { duration: 0.4, ease: "easeInOut" }
              );
            });

            // Reset shake animation after it completes
            setTimeout(() => {
              setShakeAnimation(false);

              // Make sure all words are properly snapped back to grid
              wordList.forEach((_, index) => {
                snapToGrid(index, motionValues[index].get());
              });

              // Reset animation state
              isAnimating.current = false;
            }, 500);

            // Reset error message after a delay
            setTimeout(() => {
              setInvalidWordError(false);
            }, 3000);
          }
          return;
        }

        // Play success sound for valid word
        successSound.current.play();

        // Update game state
        setCurrentGuess(extractedGuess);
        const newGuesses = [
          ...guesses,
          { word: extractedGuess, score: 1 }, // Each word counts as 1
        ];
        setGuesses(newGuesses);
        const newWordsFound = numWordsFound + 1;
        setWordsFound(newWordsFound);
        setAttempts(attempts + 1);
        setUsedWords(new Set([...usedWords, extractedGuess.toLowerCase()]));

        // NEW: Trigger juicy animation if this is a target word
        if (isValidTargetWord) {
          triggerJuicyWordFound(extractedGuess);
        }

        // Check if player has won (but can continue playing)
        if (newWordsFound >= goalWords && !hasWon) {
          setHasWon(true);
        }

        // Check if game is over due to all words found
        if (newWordsFound === totalPossible) {
          setGameOver(true);
        }

        // Check if game is over due to max attempts
        if (maxAttempts && attempts + 1 >= maxAttempts) {
          setGameOver(true);
        }

        // Log guess made
        logGuessMade("Slidewords", publicationId, {
          attempt: attempts + 1,
          word: extractedGuess,
          score: 1,
          totalScore: newWordsFound,
          date: new Date().toLocaleDateString("en-US"),
        });
      } catch (error) {
        console.error("Error validating word:", error);
        // If validation fails, still accept the word
        setCurrentGuess(extractedGuess);
        const newGuesses = [...guesses, { word: extractedGuess, score: 1 }];
        setGuesses(newGuesses);
        const newWordsFound = numWordsFound + 1;
        setWordsFound(newWordsFound);
        setAttempts(attempts + 1);
        setUsedWords(new Set([...usedWords, extractedGuess.toLowerCase()]));

        // Check if player has won (but can continue playing)
        if (newWordsFound >= goalWords && !hasWon) {
          setHasWon(true);
        }

        // Check if game is over due to max attempts
        if (maxAttempts && attempts + 1 >= maxAttempts) {
          setGameOver(true);
        }
      }
    }
  };

  // Share functionality
  const copyToClipboard = (text) => {
    return navigator.clipboard
      .writeText(text)
      .then(() => {
        setShowShareToast(true);
        setTimeout(() => {
          setShowShareToast(false);
        }, 3000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const handleShare = () => {
    const won = numWordsFound >= goalWords;

    // Block summary: 🟩 for found, ⬜ for not found
    const blockSummary = possibleWords
      .map((word) => (foundWordsSet.has(word.toUpperCase()) ? "🟩" : "⬜"))
      .join("");

    // Date string (MM/DD/YY)
    const dateStr = new Date().toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "2-digit",
    });

    // Format: "Coffee Slidewords 6/25/25\n\n🏆⬜⬜⬜🟩🟩\n\nPlay at: ..."
    let shareString = `${customTitle || title} ${dateStr}\n\n`;
    if (won) {
      shareString += `🏆${blockSummary}`;
    } else {
      shareString += blockSummary;
    }
    shareString += `\n\nPlay at: ${shareUrl}`;

    // Play button click sound
    buttonClickSound.current.play();

    copyToClipboard(shareString)
      .then(() => {
        // Log share analytics
        logGameShared(title, publicationId, {
          won: won,
          attempts: attempts,
          finalScore: numWordsFound,
          goalScore: goalWords,
          date: new Date().toLocaleDateString("en-US"),
        });
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  // When opening answers modal, set hasViewedAnswers to true
  const handleOpenAnswersModal = () => {
    // Play button click sound
    buttonClickSound.current.play();
    setShowAnswersModal(true);
    setHasViewedAnswers(true);
  };

  // Show bonus message when player first wins, then fade out after 2 seconds
  const [showBonusMessage, setShowBonusMessage] = useState(false);

  useEffect(() => {
    if (hasWon) {
      setShowBonusMessage(true);
      const timer = setTimeout(() => setShowBonusMessage(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [hasWon]);

  // Hide bonus message if user makes another guess or reveals answers (immediate)
  useEffect(() => {
    if (!hasWon) return;
    if (guesses.length > numWordsFound || hasViewedAnswers) {
      setShowBonusMessage(false);
    }
  }, [guesses.length, hasViewedAnswers, hasWon, numWordsFound]);

  // NEW: Centralized juicy word found function
  const triggerJuicyWordFound = (word) => {
    // Play success sound for finding target word
    successSound.current.play();

    // 1. Set the word for letter animations
    setJuicyWordFound(word);

    // 2. Calculate position for floating badge (center of game area)
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (containerRect) {
      setFloatingBadgeData({
        word: word,
        position: {
          x: containerRect.left + containerRect.width / 2,
          y: containerRect.top + containerRect.height / 2 - 50,
        },
      });
    }

    // 3. Create particles
    const newParticles = [];
    for (let i = 0; i < 12; i++) {
      newParticles.push({
        id: i,
        x: (Math.random() - 0.5) * 100,
        y: (Math.random() - 0.5) * 100,
        vx: (Math.random() - 0.5) * 6,
        vy: (Math.random() - 0.5) * 6 - 2, // Slight upward bias
        life: 1,
        decay: 0.015 + Math.random() * 0.01,
        color: ["#FFD700", "#FFA500", "#4CAF50", "#2196F3", "#E91E63"][
          Math.floor(Math.random() * 5)
        ],
        size: 3 + Math.random() * 4,
        type: Math.random() > 0.5 ? "circle" : "star",
      });
    }
    setParticles(newParticles);

    // 4. Trigger animations with proper timing
    setShowWordGlow(true);
    setShowParticles(true);
    setShowFloatingBadge(true);

    // 5. Animate particles
    const particleInterval = setInterval(() => {
      setParticles((prev) =>
        prev
          .map((particle) => ({
            ...particle,
            x: particle.x + particle.vx,
            y: particle.y + particle.vy,
            life: particle.life - particle.decay,
            vy: particle.vy + 0.15, // gravity
          }))
          .filter((particle) => particle.life > 0)
      );
    }, 50);

    // 6. Clean up animations
    setTimeout(() => {
      setJuicyWordFound(null);
      setShowWordGlow(false);
    }, 600); // Letter animation duration

    setTimeout(() => {
      setShowParticles(false);
      clearInterval(particleInterval);
    }, 700); // Particle lifespan

    setTimeout(() => {
      setShowFloatingBadge(false);
    }, 1000); // Badge duration

    // 7. Optional: Trigger haptic feedback on mobile
    if (navigator.vibrate) {
      navigator.vibrate(50); // Soft tap
    }
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        p: { xs: 1, sm: 0 },
        display: "flex",
        flexDirection: "column",
        position: "relative",
        width: "100%",
        backgroundColor: backgroundColor,
        minHeight: { xs: "auto", sm: "500px" },
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        {bannerPath && (
          <GameHeader
            title={customTitle || title}
            subtitle={subtitle}
            iconPath={iconPath}
            bannerPath={bannerPath}
            height="100px"
            width="350px"
          />
        )}

        {/* How to Play Dialog */}
        <Dialog
          open={showHowToPlay}
          onClose={() => {
            buttonClickSound.current.play();
            setShowHowToPlay(false);
          }}
          maxWidth="sm"
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 600 }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 0,
              m: 2,
              maxWidth: { xs: "90%", sm: "400px" },
              position: "relative",
              overflow: "visible",
              bgcolor: "white",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              pt: 3,
              pb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              position: "relative",
            }}
          >
            <Typography
              variant="h5"
              component="span"
              sx={{
                fontWeight: 600,
                fontSize: "1.5rem",
                color: "black",
              }}
            >
              How to play
            </Typography>
            <IconButton
              onClick={() => {
                buttonClickSound.current.play();
                setShowHowToPlay(false);
              }}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "black",
              }}
            >
              <CloseIcon />
            </IconButton>
            {externalIsMuted === undefined && (
              <Tooltip title={effectiveIsMuted ? "Unmute" : "Mute"}>
                <IconButton
                  onClick={() => {
                    buttonClickSound.current.play();
                    toggleMute();
                  }}
                  sx={{
                    position: "absolute",
                    right: 48,
                    top: 8,
                    color: "black",
                  }}
                >
                  {effectiveIsMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
              </Tooltip>
            )}
          </DialogTitle>

          <DialogContent sx={{ px: 4, pb: 4, pt: 1 }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mr: 2,
                  }}
                >
                  <img
                    src="/assets/icons/slide.svg"
                    alt="Slide icon"
                    style={{
                      width: "32px",
                      height: "32px",
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: "1rem", color: "black" }}>
                  Drag words horizontally to form new words, aligned vertically.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box sx={{ mr: 2 }}>
                  <Check size={24} />
                </Box>
                <Typography sx={{ fontSize: "1rem", color: "black" }}>
                  Tap "Check" to submit your word and count it.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Box sx={{ mr: 2 }}>
                  <Star size={24} />
                </Box>
                <Typography sx={{ fontSize: "1rem", color: "black" }}>
                  Each valid word counts as 1 point.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ mr: 2 }}>
                  <Trophy size={24} />
                </Box>
                <Typography sx={{ fontSize: "1rem", color: "black" }}>
                  Reach {goalWords} words to win!
                </Typography>
              </Box>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                buttonClickSound.current.play();
                setShowHowToPlay(false);
              }}
              sx={{
                bgcolor: buttonColor,
                color: "white",
                borderRadius: "24px",
                py: 1.5,
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "black",
                  opacity: 0.9,
                },
              }}
            >
              Play!
            </Button>
          </DialogContent>
        </Dialog>

        {/* Victory Modal */}
        <Dialog
          open={showVictoryModal}
          onClose={() => {
            buttonClickSound.current.play();
            setShowVictoryModal(false);
            setVictoryModalDismissed(true);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              overflow: "hidden",
              bgcolor: modalBackgroundColor,
              color: "black",
              borderRadius: "12px",
              textAlign: "left",
              p: { xs: 3, sm: 4 },
              m: 2,
              maxWidth: { xs: "90%", sm: "400px" },
              width: "100%",
              border: "1px solid black",
              position: "relative",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            {showConfetti && (
              <Confetti
                width={containerWidth || 400}
                height={500}
                recycle={false}
                numberOfPieces={confettiPieces}
                gravity={0.15}
                opacity={confettiPieces < 100 ? confettiPieces / 100 : 1}
                confineToParentElement={true}
                colors={[
                  "#FFA500",
                  "#FF4500",
                  "#FFD700",
                  "#FFFF00",
                  "#00BFFF",
                  "#1E90FF",
                  "#ADD8E6",
                ]}
              />
            )}

            <IconButton
              onClick={() => {
                buttonClickSound.current.play();
                setShowVictoryModal(false);
                setVictoryModalDismissed(true);
              }}
              aria-label="close"
              sx={{
                position: "absolute",
                right: 16,
                top: 16,
                color: "black",
                zIndex: 2001,
                pointerEvents: "auto",
                width: { xs: 48, sm: 40 },
                height: { xs: 48, sm: 40 },
                transition: "transform 0.2s ease",
                backgroundColor: "rgba(255,255,255,0.7)",
                "&:hover": {
                  transform: "rotate(90deg)",
                  bgcolor: "rgba(0, 0, 0, 0.08)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: 32, sm: 28 } }} />
            </IconButton>

            {/* Dynamic Victory Message */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  pt: 3,
                  mt: 3,
                  fontWeight: "bold",
                  color: "black",
                  fontSize: { xs: "2.2rem", sm: "2.2rem" },
                  textAlign: "center",
                }}
              >
                {hasViewedAnswers
                  ? "That's all, folks!"
                  : allFound
                  ? "🏁 You found all 5 words!"
                  : "YOU WON!"}
              </Typography>
            </motion.div>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <Box sx={{ mb: 3, textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{
                    mb: 2,
                    fontWeight: "bold",
                    color: "black",
                    fontSize: { xs: "1.8rem", sm: "2rem" },
                  }}
                >
                  Words Found: {numWordsFound}/{totalPossible}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "black",
                    opacity: 0.9,
                    fontSize: { xs: "1.1rem", sm: "1.2rem" },
                    mb: 2,
                  }}
                >
                  {hasViewedAnswers
                    ? "You met the goal and revealed all answers for today's puzzle."
                    : allFound
                    ? "You crushed it. Come back tomorrow for a new puzzle!"
                    : `Goal achieved! There are still ${remaining} words left to find.`}
                </Typography>
                {!allFound && !hasViewedAnswers && (
                  <Typography variant="body1" sx={{ color: "black", mb: 1 }}>
                    Can you find the remaining {remaining}?
                  </Typography>
                )}
                {hasViewedAnswers && (
                  <Typography
                    variant="body2"
                    sx={{ color: "#333", mb: 1, fontStyle: "italic" }}
                  >
                    You found {numWordsFound} out of {totalPossible} words
                    before revealing.
                  </Typography>
                )}
              </Box>
            </motion.div>

            {/* Share Button Preview */}
            {!allFound && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Button
                    startIcon={<SendIcon />}
                    variant="contained"
                    onClick={handleShare}
                    sx={{
                      bgcolor: "black",
                      color: "white",
                      fontWeight: "bold",
                      borderRadius: "30px",
                      px: 4,
                      py: 1.5,
                      mb: 0,
                      width: "80%",
                      textTransform: "none",
                      transition: "all 0.2s ease",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#333",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                      },
                    }}
                  >
                    Share my score
                  </Button>
                </Box>
              </motion.div>
            )}

            {/* Play Tomorrow's Puzzle Button (only when all words found) */}
            {allFound && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={() => {
                      // Play button click sound
                      buttonClickSound.current.play();
                      // Close modal and potentially refresh or redirect
                      setShowVictoryModal(false);
                      setVictoryModalDismissed(true);
                    }}
                    sx={{
                      bgcolor: "black",
                      color: "white",
                      fontWeight: "bold",
                      borderRadius: "30px",
                      px: 4,
                      py: 1.5,
                      mb: 2,
                      width: "80%",
                      textTransform: "none",
                      transition: "all 0.2s ease",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#333",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                      },
                    }}
                  >
                    Play Tomorrow's Puzzle
                  </Button>
                </Box>
              </motion.div>
            )}

            {/* Answers Modal */}
            <Dialog
              open={showAnswersModal}
              onClose={() => {
                buttonClickSound.current.play();
                setShowAnswersModal(false);
              }}
              maxWidth="xs"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  p: 2,
                  bgcolor: "#fafafa",
                  maxHeight: 400,
                  overflowY: "auto",
                },
              }}
            >
              <DialogTitle
                sx={{ textAlign: "center", fontWeight: 700, color: "black" }}
              >
                All Answers
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {possibleWords.map((word) => (
                    <Typography
                      key={word}
                      sx={{
                        color: foundWordsSet.has(word.toUpperCase())
                          ? "#388e3c"
                          : "#333",
                        fontWeight: foundWordsSet.has(word.toUpperCase())
                          ? 700
                          : 400,
                        fontSize: "1.1rem",
                        letterSpacing: 1,
                      }}
                    >
                      {word}
                      {foundWordsSet.has(word.toUpperCase()) && " ✓"}
                    </Typography>
                  ))}
                </Box>
              </DialogContent>
            </Dialog>
          </DialogContent>
          {/* Toast notification for share to clipboard */}
          <AnimatePresence>
            {showShareToast && (
              <motion.div
                initial={{ opacity: 0, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#43a047",
                  color: "white",
                  fontWeight: "bold",
                  minHeight: "48px",
                  pointerEvents: "none",
                }}
              >
                <span
                  style={{
                    backgroundColor: "white",
                    borderRadius: "50%",
                    width: 22,
                    height: 22,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 8,
                    fontSize: 15,
                    color: "#43a047",
                    lineHeight: 1,
                  }}
                >
                  ✓
                </span>
                Results copied to clipboard!
              </motion.div>
            )}
          </AnimatePresence>
        </Dialog>

        {/* Word sliding area */}
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            maxWidth: "sm",
            mx: "auto",
            height: `${wordList.length * 70 + 13}px`,
            mb: { xs: 2, sm: 3 },
            overflow: "hidden",
            borderRadius: "12px",
            border: "1px solid rgba(0, 0, 0, 0.15)",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            backgroundColor: "white",
            "@media (max-height: 700px)": {
              mb: 1.5,
            },
          }}
        >
          {/* Loading placeholder when layout is not ready */}
          {!isLayoutReady && (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                opacity: 0.7,
              }}
            >
              {/* Visual placeholder for the rows */}
              {Array.from({ length: wordList.length }).map((_, index) => (
                <Box
                  key={`placeholder-${index}`}
                  sx={{
                    width: "80%",
                    height: "60px",
                    my: 1.5,
                    borderRadius: "4px",
                    backgroundColor: "rgba(0,0,0,0.05)",
                  }}
                />
              ))}
            </Box>
          )}

          {/* Conditional rendering with fade-in animation */}
          <AnimatePresence>
            {isLayoutReady && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                style={{ width: "100%", height: "100%" }}
              >
                {/* Center column - static letter boxes */}
                <Box
                  sx={{
                    position: "absolute",
                    left: "50%",
                    top: 0,
                    height: "100%",
                    width: letterWidth,
                    transform: "translateX(-50%)",
                    zIndex: 1,
                    pointerEvents: "none", // Ensure it doesn't interfere with dragging
                  }}
                >
                  {/* Individual letter boxes in center column - now just borders */}
                  {wordList.map((_, wordIndex) => (
                    <Box
                      key={`center-box-${wordIndex}`}
                      sx={{
                        position: "absolute",
                        width: letterWidth,
                        height: "60px",
                        top: `${wordIndex * 70 + 10}px`,
                        border: "4px solid black",
                        borderRight: "3px solid black",
                        zIndex: 0,
                      }}
                    />
                  ))}
                </Box>

                {/* Words that can be dragged */}
                {wordList.map((word, wordIndex) => {
                  const xMotionValue = motionValues[wordIndex];
                  const wordWidth = word.length * letterWidth;

                  return (
                    <motion.div
                      key={`word-${wordIndex}`}
                      style={{
                        x: xMotionValue,
                        position: "absolute",
                        top: `${wordIndex * 70 + 10}px`,
                        height: "60px",
                        display: "flex",
                        touchAction: "none",
                        cursor: "grab",
                      }}
                      whileDrag={{ cursor: "grabbing" }}
                      drag="x"
                      dragConstraints={{
                        left: -(wordWidth - containerWidth / 2),
                        right: containerWidth / 2 - letterWidth,
                      }}
                      dragElastic={0.1}
                      dragMomentum={false}
                      onDragEnd={() =>
                        snapToGrid(wordIndex, xMotionValue.get())
                      }
                    >
                      {Array.from(word).map((letter, letterIndex) => (
                        <Box
                          key={`letter-${wordIndex}-${letterIndex}`}
                          sx={{
                            width: letterWidth,
                            height: "60px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "white",
                            border: "2px solid black",
                            borderRight: "1px solid black",
                            borderLeft: "1px solid black",
                            borderBottom: "1px solid black",
                            borderTop: "1px solid black",
                            color: "black",
                            fontSize: "1.5rem",
                            fontWeight: 400,
                            position: "relative",
                            overflow: "visible",
                          }}
                        >
                          <motion.span
                            key={
                              juicyWordFound && showWordGlow
                                ? `bounce-${juicyWordFound}-${letterIndex}`
                                : `static-${letterIndex}`
                            }
                            initial={{ scale: 1 }}
                            animate={
                              juicyWordFound && showWordGlow
                                ? {
                                    scale: [1, 1.2, 1],
                                    transition: {
                                      duration: 0.6,
                                      delay: letterIndex * 0.075,
                                      ease: [0.175, 0.885, 0.32, 1.275],
                                    },
                                  }
                                : { scale: 1 }
                            }
                            style={{
                              display: "inline-block",
                              willChange: "transform",
                              transformOrigin: "center center",
                            }}
                          >
                            {letter}
                          </motion.span>
                        </Box>
                      ))}
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </Box>

        {/* NEW: Juicy Animation Components */}

        {/* Word Glow Highlight */}
        <AnimatePresence>
          {showWordGlow && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.8, 0] }}
              transition={{ duration: 0.4 }}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: "200px",
                height: "60px",
                background:
                  "radial-gradient(ellipse, rgba(76, 175, 80, 0.3) 0%, transparent 70%)",
                pointerEvents: "none",
                zIndex: 998,
              }}
            />
          )}
        </AnimatePresence>

        {/* Particle Burst */}
        {showParticles && (
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              zIndex: 1000,
            }}
          >
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                initial={{ opacity: 1, scale: 1 }}
                animate={{
                  opacity: particle.life,
                  scale: particle.life * 0.5 + 0.5,
                }}
                style={{
                  position: "absolute",
                  left: particle.x,
                  top: particle.y,
                  width: particle.size,
                  height: particle.size,
                  backgroundColor: particle.color,
                  borderRadius: particle.type === "circle" ? "50%" : "0%",
                  transform:
                    particle.type === "star" ? "rotate(45deg)" : "none",
                  boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
                }}
              />
            ))}
          </Box>
        )}

        {/* Floating Badge */}
        <AnimatePresence>
          {showFloatingBadge && (
            <motion.div
              initial={{ opacity: 0, y: 0, scale: 0.8 }}
              animate={{ opacity: 1, y: -20, scale: 1 }}
              exit={{ opacity: 0, y: -40, scale: 0.8 }}
              transition={{
                duration: 1,
                ease: "easeOut",
              }}
              style={{
                position: "fixed",
                left: floatingBadgeData.position.x,
                top: floatingBadgeData.position.y,
                transform: "translate(-50%, -50%)",
                zIndex: 1001,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  background: "linear-gradient(135deg, #4CAF50, #45a049)",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  boxShadow: "0 4px 16px rgba(76, 175, 80, 0.4)",
                  border: "2px solid #45a049",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                ✅ {floatingBadgeData.word} Found!
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Collapsible Word Tracker UI */}
        <Box
          sx={{
            mb: { xs: 2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            maxWidth: "sm",
            mx: "auto",
            width: "100%",
            "@media (max-height: 700px)": {
              mb: 1.5,
              gap: 0.5,
            },
          }}
        >
          {/* Collapsible header */}
          <Button
            onClick={() => {
              buttonClickSound.current.play();
              setFoundWordsExpanded(!foundWordsExpanded);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#666",
              textTransform: "none",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              fontWeight: 500,
              p: 1,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <span>
              🔍 Found Words ({numWordsFound}/{totalPossible})
            </span>
            <span
              style={{
                transform: foundWordsExpanded
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            >
              ▼
            </span>
          </Button>

          {/* Expanded content */}
          <Collapse in={foundWordsExpanded}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(3, 1fr)",
                  sm: "repeat(3, 1fr)",
                },
                gap: 1,
                opacity: 0.8,
                width: "100%",
                maxWidth: "340px",
                mb: 1,
              }}
            >
              {possibleWords.map((word, index) => {
                const isFound = foundWordsSet.has(word.toUpperCase());
                const isRevealed = hasViewedAnswers;
                const isRecentlyFound =
                  guesses.length > 0 &&
                  guesses[guesses.length - 1]?.word?.toUpperCase() ===
                    word.toUpperCase();

                return (
                  <motion.div
                    key={`word-tracker-${index}`}
                    initial={
                      isRecentlyFound ? { scale: 0.8, opacity: 0 } : false
                    }
                    animate={isRecentlyFound ? { scale: 1, opacity: 1 } : false}
                    transition={
                      isRecentlyFound
                        ? {
                            type: "spring",
                            stiffness: 400,
                            damping: 20,
                            delay: 0.5, // Delay to sync with success animation
                          }
                        : {}
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: { xs: "0.85rem", sm: "0.9rem" },
                        color: isFound ? "#4CAF50" : "#999",
                        fontWeight: isFound ? 600 : 400,
                        p: 0.5,
                        borderRadius: "4px",
                        backgroundColor: isFound
                          ? "rgba(76, 175, 80, 0.1)"
                          : "transparent",
                        justifyContent: "flex-start",
                        minWidth: 0,
                        wordBreak: "break-word",
                        border: isRecentlyFound ? "2px solid #4CAF50" : "none",
                        boxShadow: isRecentlyFound
                          ? "0 0 8px rgba(76, 175, 80, 0.3)"
                          : "none",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <motion.span
                        style={{ fontSize: "1rem" }}
                        animate={
                          isRecentlyFound
                            ? {
                                scale: [1, 1.3, 1],
                                rotate: [0, 10, -10, 0],
                              }
                            : {}
                        }
                        transition={
                          isRecentlyFound
                            ? {
                                duration: 0.6,
                                delay: 0.7,
                              }
                            : {}
                        }
                      >
                        {isFound ? "✅" : isRevealed ? "⬜" : "⬜"}
                      </motion.span>
                      <span>{isFound || isRevealed ? word : "_____"}</span>
                    </Box>
                  </motion.div>
                );
              })}
            </Box>
            {/* Progress bar inside expanded section */}
            <Box
              sx={{
                width: "100%",
                height: "4px",
                backgroundColor: "rgba(0, 0, 0, 0.1)",
                borderRadius: "2px",
                overflow: "hidden",
                mt: 1,
                mb: 1,
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(numWordsFound / totalPossible) * 100}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                style={{
                  height: "100%",
                  backgroundColor: "#4CAF50",
                  borderRadius: "2px",
                }}
              />
            </Box>
            {/* Reveal All Answers Button - only show when appropriate */}
            {numWordsFound >= goalWords &&
              numWordsFound < totalPossible &&
              !hasViewedAnswers && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    buttonClickSound.current.play();
                    setShowRevealConfirm(true);
                  }}
                  sx={{
                    mt: 1,
                    borderRadius: "20px",
                    fontWeight: 600,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    py: { xs: 0.8, sm: 1 },
                  }}
                >
                  Reveal All Answers
                </Button>
              )}
          </Collapse>
        </Box>

        {/* Check/Share button */}
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <Button
            variant="contained"
            color="primary"
            disableRipple
            startIcon={hasViewedAnswers || allFound ? <SendIcon /> : undefined}
            sx={{
              px: { xs: 3, sm: 4 },
              py: { xs: 1.2, sm: 1 },
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: "1.1rem", sm: "1.2rem" },
              borderRadius: "20px",
              width: { xs: "85%", sm: "75%" },
              maxWidth: "300px",
              bgcolor: buttonColor,
              "&.MuiButton-contained": {
                bgcolor: buttonColor,
              },
              "&.Mui-disabled": {
                bgcolor: buttonColor,
                opacity: 0.6,
                color: "white",
              },
              "@media (max-height: 700px)": {
                mb: 1.5,
                py: 1,
                fontSize: "1rem",
              },
            }}
            onClick={() => {
              if (hasViewedAnswers || allFound) {
                handleShare();
                setShowShareToast(true);
                setTimeout(() => setShowShareToast(false), 3000);
              } else {
                // Play click sound for check button
                clickSound.current.play();
                checkGuess();
              }
            }}
            disabled={
              !hasViewedAnswers &&
              !allFound &&
              (gameOver ||
                !isLayoutReady ||
                (maxAttempts && attempts >= maxAttempts))
            }
          >
            {hasViewedAnswers || allFound ? "Share" : "Check"}
          </Button>
        </motion.div>

        {/* Toast notification for share to clipboard (main UI) */}
        <AnimatePresence>
          {showShareToast && (
            <motion.div
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              style={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#43a047",
                color: "white",
                fontWeight: "bold",
                minHeight: "48px",
                pointerEvents: "none",
              }}
            >
              <span
                style={{
                  backgroundColor: "white",
                  borderRadius: "50%",
                  width: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 8,
                  fontSize: 15,
                  color: "#43a047",
                  lineHeight: 1,
                }}
              >
                ✓
              </span>
              Results copied to clipboard!
            </motion.div>
          )}
        </AnimatePresence>

        {/* Success indicator when player has won */}
        <Fade
          in={hasWon && showBonusMessage}
          timeout={{ enter: 300, exit: 600 }}
          unmountOnExit
        >
          <div>
            <Box
              sx={{
                mb: { xs: 1.5, sm: 2 },
                p: { xs: 1.5, sm: 2 },
                borderRadius: "12px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                border: "2px solid #4CAF50",
                textAlign: "center",
                maxWidth: "sm",
                mx: "auto",
              }}
            >
              <Typography
                sx={{
                  color: "#2E7D32",
                  fontWeight: 600,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  lineHeight: 1.4,
                }}
              >
                🎉 You found {numWordsFound} of {totalPossible} words! <br />
                🧠 Bonus challenge: find all {totalPossible} hidden words.
              </Typography>
            </Box>
          </div>
        </Fade>

        {/* Reveal Confirmation Dialog */}
        <Dialog
          open={showRevealConfirm}
          onClose={() => setShowRevealConfirm(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, p: 2 } }}
        >
          <DialogTitle
            sx={{ textAlign: "center", fontWeight: 700, color: "black" }}
          >
            Reveal All Answers?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: "#333", mb: 2, textAlign: "center" }}>
              Are you sure you want to reveal all answers? <br />
              <b>You won't be able to keep playing.</b>
            </Typography>
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  buttonClickSound.current.play();
                  setShowRevealConfirm(false);
                }}
                sx={{ borderRadius: "20px", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  buttonClickSound.current.play();
                  setShowRevealConfirm(false);
                  setHasViewedAnswers(true);
                  setShowVictoryModal(true);
                  setShowConfetti(true);
                }}
                sx={{ borderRadius: "20px", fontWeight: 600 }}
              >
                Reveal
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default BaseSlidewords;
